import { type FC, type ReactElement, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RepeatIcon from "@mui/icons-material/Repeat";
import SensorsIcon from "@mui/icons-material/Sensors";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { LiveInputSessionOutcomeType } from "@/types/graphql/partner.generated";
import { eventCodeLabelKey } from "../model/eventCodes";
import { buildEventLogRows, type EventLogRow } from "../model/eventLog";
import { formatDuration, formatMoment } from "../model/format";
import type {
  LiveInputEvent,
  LiveInputOutages,
  LiveInputSession,
} from "../model/types";

interface MonitoringEventLogProps {
  events: LiveInputEvent[];
  sessions: LiveInputSession[];
  outages: LiveInputOutages | null;
  loading?: boolean;
}

// Ключ i18n только статический литерал: исход сессии через мапу.
const OUTCOME_LABEL_KEYS: Record<LiveInputSessionOutcomeType, string> = {
  [LiveInputSessionOutcomeType.EndedType]:
    "streams.monitoring.log.outcome.ENDED",
  [LiveInputSessionOutcomeType.DisconnectedType]:
    "streams.monitoring.log.outcome.DISCONNECTED",
  [LiveInputSessionOutcomeType.TimeoutType]:
    "streams.monitoring.log.outcome.TIMEOUT",
  [LiveInputSessionOutcomeType.OngoingType]:
    "streams.monitoring.log.outcome.ONGOING",
};

// Иконка исхода: штатно, энкодер отключился, обрыв по таймауту, идёт.
const OUTCOME_ICONS: Record<LiveInputSessionOutcomeType, ReactElement> = {
  [LiveInputSessionOutcomeType.EndedType]: (
    <CheckCircleOutlineIcon color="success" />
  ),
  [LiveInputSessionOutcomeType.DisconnectedType]: (
    <LinkOffIcon color="warning" />
  ),
  [LiveInputSessionOutcomeType.TimeoutType]: (
    <TimerOffOutlinedIcon color="error" />
  ),
  [LiveInputSessionOutcomeType.OngoingType]: <SensorsIcon color="success" />,
};

/** Высота развёрнутого журнала: прокрутка внутри, страница не растёт. */
const LOG_MAX_HEIGHT = 420;

const seconds = (from: string, to: string | null | undefined) =>
  ((to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime()) /
  1000;

const EventRows: FC<{ events: LiveInputEvent[] }> = ({ events }) => {
  const { t, i18n } = useTranslation();
  if (events.length === 0) {
    return (
      <ListItem sx={{ pl: 4 }}>
        <ListItemText secondary={t("streams.monitoring.log.noSessionEvents")} />
      </ListItem>
    );
  }
  return (
    <>
      {events.map((event, index) => {
        const labelKey = eventCodeLabelKey(event.code);
        return (
          <ListItem key={`${event.at}-${event.code}-${index}`} sx={{ pl: 4 }}>
            <ListItemIcon>
              {event.isWarning ? (
                <WarningAmberIcon fontSize="small" color="warning" />
              ) : (
                <InfoOutlinedIcon fontSize="small" color="action" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={labelKey ? t(labelKey) : event.code}
              secondary={`${formatMoment(event.at, i18n.language)} ${
                labelKey ? "" : event.description
              }`.trim()}
            />
          </ListItem>
        );
      })}
    </>
  );
};

const LogRowItem: FC<{ row: EventLogRow }> = ({ row }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const lng = i18n.language;

  let icon: ReactElement;
  let primary: string;
  let secondary: string;
  let startupNote = false;

  if (row.kind === "reconnects") {
    icon = <RepeatIcon color="warning" />;
    primary = t("streams.monitoring.log.reconnects", {
      count: row.count,
      start: formatMoment(row.start, lng),
      end: row.end ? formatMoment(row.end, lng) : "",
    });
    secondary = formatDuration(t, seconds(row.start, row.end));
  } else {
    const { session } = row;
    icon = OUTCOME_ICONS[session.outcome];
    primary = session.end
      ? t("streams.monitoring.log.session.closed", {
          start: formatMoment(session.start, lng),
          end: formatMoment(session.end, lng),
        })
      : t("streams.monitoring.log.session.open", {
          start: formatMoment(session.start, lng),
        });
    const parts = [
      formatDuration(t, seconds(session.start, session.end)),
      t(OUTCOME_LABEL_KEYS[session.outcome]),
    ];
    if (session.warningCount > 0) {
      parts.push(
        t("streams.monitoring.log.session.warnings", {
          count: session.warningCount,
        })
      );
    }
    secondary = parts.join(", ");
    startupNote = session.hadStartupWarnings;
  }

  return (
    <>
      <ListItemButton onClick={() => setOpen((value) => !value)}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText
          primary={primary}
          secondary={
            <Stack
              component="span"
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              <span>{secondary}</span>
              {startupNote ? (
                <Chip
                  component="span"
                  size="small"
                  variant="outlined"
                  label={t("streams.monitoring.log.session.startupWarnings")}
                />
              ) : null}
            </Stack>
          }
          slotProps={{ secondary: { component: "span" } }}
        />
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={open} unmountOnExit>
        <List dense disablePadding>
          <EventRows events={row.events} />
        </List>
      </Collapse>
    </>
  );
};

/**
 * Журнал входа (спека 2026-10-24-stream-monitoring-ux, часть 2, п. 3):
 * в самом низу вкладки, свёрнут по умолчанию, в шапке строка-итог. Внутри
 * сессии подключения, события сессии раскрываются по клику.
 */
export const MonitoringEventLog: FC<MonitoringEventLogProps> = ({
  events,
  sessions,
  outages,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();
  const rows = useMemo(
    () => buildEventLogRows(sessions, events),
    [sessions, events]
  );

  const eventsLabel = t("streams.monitoring.log.events", {
    count: events.length,
  });
  const outagesLabel = t("streams.monitoring.log.outages", {
    count: outages?.count ?? 0,
  });
  const summary = outages?.lastAt
    ? t("streams.monitoring.log.summaryWithLast", {
        events: eventsLabel,
        outages: outagesLabel,
        last: formatMoment(outages.lastAt, i18n.language),
      })
    : t("streams.monitoring.log.summary", {
        events: eventsLabel,
        outages: outagesLabel,
      });

  let body;
  if (loading && events.length === 0) {
    body = <Skeleton variant="rounded" height={56} />;
  } else if (rows.length === 0 && events.length > 0) {
    // Сессий нет (Cloudflare не прислал подключений в периоде): события
    // плоским списком, старые первыми.
    body = (
      <List
        dense
        disablePadding
        sx={{ maxHeight: LOG_MAX_HEIGHT, overflow: "auto" }}
      >
        <EventRows
          events={[...events].sort(
            (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
          )}
        />
      </List>
    );
  } else if (rows.length === 0) {
    body = (
      <Typography color="text.secondary">
        {t("streams.monitoring.log.empty")}
      </Typography>
    );
  } else {
    body = (
      <List
        dense
        disablePadding
        sx={{ maxHeight: LOG_MAX_HEIGHT, overflow: "auto" }}
      >
        {rows.map((row) => (
          <LogRowItem key={row.key} row={row} />
        ))}
      </List>
    );
  }

  return (
    <Accordion disableGutters defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">{summary}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>{body}</AccordionDetails>
    </Accordion>
  );
};
